import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Key, 
  Eye, 
  EyeOff, 
  Save, 
  RefreshCw, 
  AlertCircle,
  CheckCircle2,
  CreditCard,
  Mail,
  Smartphone,
  Apple,
  Copy,
  Trash2
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/hooks/useLanguage';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

interface APIKey {
  id: string;
  service_name: string;
  key_name: string;
  key_value: string;
  environment: string; // Changé de 'test' | 'live' en string pour compatibilité DB
  is_active: boolean;
  description: string;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

interface ServiceConfig {
  name: string;
  icon: any;
  color: string;
  keys: {
    name: string;
    label: string;
    placeholder: string;
    type: 'text' | 'textarea';
    validation?: (value: string) => boolean;
  }[];
}

const SERVICES: Record<string, ServiceConfig> = {
  stripe: {
    name: 'Stripe',
    icon: CreditCard,
    color: 'text-purple-600',
    keys: [
      {
        name: 'public_key',
        label: 'Clé Publique',
        placeholder: 'pk_test_... ou pk_live_...',
        type: 'text',
        validation: (v) => v.startsWith('pk_test_') || v.startsWith('pk_live_')
      },
      {
        name: 'secret_key',
        label: 'Clé Secrète',
        placeholder: 'sk_test_... ou sk_live_...',
        type: 'text',
        validation: (v) => v.startsWith('sk_test_') || v.startsWith('sk_live_')
      },
      {
        name: 'webhook_secret',
        label: 'Secret Webhook',
        placeholder: 'whsec_...',
        type: 'text',
        validation: (v) => v.startsWith('whsec_')
      }
    ]
  },
  resend: {
    name: 'Resend',
    icon: Mail,
    color: 'text-blue-600',
    keys: [
      {
        name: 'api_key',
        label: 'Clé API',
        placeholder: 're_...',
        type: 'text',
        validation: (v) => v.startsWith('re_')
      }
    ]
  },
  firebase: {
    name: 'Firebase',
    icon: Smartphone,
    color: 'text-orange-600',
    keys: [
      {
        name: 'service_account',
        label: 'Service Account JSON',
        placeholder: '{"type":"service_account",...}',
        type: 'textarea',
        validation: (v) => {
          try {
            const json = JSON.parse(v);
            return json.type === 'service_account';
          } catch {
            return false;
          }
        }
      }
    ]
  },
  apns: {
    name: 'Apple Push Notification',
    icon: Apple,
    color: 'text-gray-800',
    keys: [
      {
        name: 'key',
        label: 'Clé Privée',
        placeholder: '-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----',
        type: 'textarea',
        validation: (v) => v.includes('BEGIN PRIVATE KEY')
      },
      {
        name: 'key_id',
        label: 'Key ID',
        placeholder: 'ABC123XYZ',
        type: 'text'
      },
      {
        name: 'team_id',
        label: 'Team ID',
        placeholder: 'DEF456UVW',
        type: 'text'
      }
    ]
  }
};

export function APIKeysManager() {
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeService, setActiveService] = useState('stripe');
  const [environment, setEnvironment] = useState<'test' | 'live'>('test');
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());
  const [editedKeys, setEditedKeys] = useState<Record<string, string>>({});
  const { toast } = useToast();
  const { t } = useLanguage();

  useEffect(() => {
    fetchAPIKeys();
  }, []);

  const fetchAPIKeys = async () => {
    try {
      const { data, error } = await supabase
        .from('api_keys')
        .select('*')
        .order('service_name', { ascending: true })
        .order('key_name', { ascending: true });

      if (error) throw error;
      setApiKeys(data || []);
    } catch (error) {
      console.error('Error fetching API keys:', error);
      toast({
        title: t('common.error'),
        description: 'Erreur lors du chargement des clés API',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getKeyId = (service: string, keyName: string, env: 'test' | 'live') => {
    return `${service}::${keyName}::${env}`;
  };

  const getKeyValue = (service: string, keyName: string, env: 'test' | 'live'): string => {
    const keyId = getKeyId(service, keyName, env);
    if (editedKeys[keyId] !== undefined) {
      return editedKeys[keyId];
    }
    const key = apiKeys.find(
      k => k.service_name === service && k.key_name === keyName && k.environment === env
    );
    return key?.key_value || '';
  };

  const setKeyValue = (service: string, keyName: string, env: 'test' | 'live', value: string) => {
    const keyId = getKeyId(service, keyName, env);
    setEditedKeys(prev => ({ ...prev, [keyId]: value }));
  };

  const toggleVisibility = (keyId: string) => {
    setVisibleKeys(prev => {
      const newSet = new Set(prev);
      if (newSet.has(keyId)) {
        newSet.delete(keyId);
      } else {
        newSet.add(keyId);
      }
      return newSet;
    });
  };

  const maskKey = (value: string, visible: boolean): string => {
    if (visible || !value) return value;
    if (value.length <= 12) return '••••••••';
    return value.substring(0, 7) + '••••••••' + value.substring(value.length - 4);
  };

  const copyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value);
    toast({
      title: 'Copié',
      description: 'La clé a été copiée dans le presse-papiers',
    });
  };

  const validateKey = (service: string, keyName: string, value: string): boolean => {
    const serviceConfig = SERVICES[service];
    const keyConfig = serviceConfig?.keys.find(k => k.name === keyName);
    if (!keyConfig?.validation) return true;
    return keyConfig.validation(value);
  };

  const saveKeys = async () => {
    setSaving(true);
    try {
      // Validate all edited keys
      for (const [keyId, value] of Object.entries(editedKeys)) {
        const [service, keyName] = keyId.split('_');
        if (value && !validateKey(service, keyName, value)) {
          toast({
            title: t('common.error'),
            description: `Format invalide pour ${SERVICES[service]?.name} - ${keyName}`,
            variant: 'destructive',
          });
          setSaving(false);
          return;
        }
      }

      // Save each edited key
      for (const [keyId, value] of Object.entries(editedKeys)) {
        const [service, keyName, env] = keyId.split('::');
        
        const { data: { user } } = await supabase.auth.getUser();
        
        const { error } = await supabase
          .from('api_keys')
          .upsert({
            service_name: service,
            key_name: keyName,
            environment: env as 'test' | 'live',
            key_value: value,
            updated_by: user?.id,
            description: SERVICES[service]?.keys.find(k => k.name === keyName)?.label || ''
          }, {
            onConflict: 'service_name,key_name,environment'
          });

        if (error) throw error;
      }

      toast({
        title: 'Succès',
        description: 'Les clés API ont été sauvegardées avec succès',
      });

      setEditedKeys({});
      await fetchAPIKeys();
    } catch (error) {
      console.error('Error saving API keys:', error);
      toast({
        title: t('common.error'),
        description: 'Erreur lors de la sauvegarde des clés',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleEnvironment = async (service: string, env: 'test' | 'live') => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Deactivate all keys for this service
      await supabase
        .from('api_keys')
        .update({ is_active: false })
        .eq('service_name', service);

      // Activate keys for the selected environment
      await supabase
        .from('api_keys')
        .update({ 
          is_active: true,
          updated_by: user?.id 
        })
        .eq('service_name', service)
        .eq('environment', env);

      toast({
        title: 'Environnement changé',
        description: `${SERVICES[service]?.name} utilise maintenant l'environnement ${env.toUpperCase()}`,
      });

      await fetchAPIKeys();
    } catch (error) {
      console.error('Error toggling environment:', error);
      toast({
        title: t('common.error'),
        description: 'Erreur lors du changement d\'environnement',
        variant: 'destructive',
      });
    }
  };

  const getActiveEnvironment = (service: string): 'test' | 'live' => {
    const activeKey = apiKeys.find(k => k.service_name === service && k.is_active);
    return activeKey?.environment || 'test';
  };

  const hasChanges = Object.keys(editedKeys).length > 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const serviceConfig = SERVICES[activeService];
  const Icon = serviceConfig?.icon || Key;
  const activeEnv = getActiveEnvironment(activeService);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Key className="w-7 h-7" />
          {t('admin.api_keys.title')}
        </h2>
        <p className="text-muted-foreground mt-1">
          {t('admin.api_keys.description')}
        </p>
      </div>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Sécurité</AlertTitle>
        <AlertDescription>
          Les clés API sont stockées de manière sécurisée. Ne partagez jamais vos clés de production.
          Seuls les administrateurs peuvent voir et modifier ces clés.
        </AlertDescription>
      </Alert>

      <Tabs value={activeService} onValueChange={setActiveService}>
        <TabsList className="grid w-full grid-cols-4">
          {Object.entries(SERVICES).map(([key, config]) => {
            const ServiceIcon = config.icon;
            return (
              <TabsTrigger key={key} value={key} className="flex items-center gap-2">
                <ServiceIcon className={`w-4 h-4 ${config.color}`} />
                {config.name}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {Object.entries(SERVICES).map(([serviceKey, config]) => (
          <TabsContent key={serviceKey} value={serviceKey} className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Icon className={`w-6 h-6 ${config.color}`} />
                    <div>
                      <CardTitle>{config.name}</CardTitle>
                      <CardDescription>
                        Configuration des clés API pour {config.name}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge variant={activeEnv === 'test' ? 'default' : 'secondary'}>
                      {activeEnv === 'test' ? 'TEST' : 'LIVE'}
                    </Badge>
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`env-${serviceKey}`} className="text-sm">
                        Mode LIVE
                      </Label>
                      <Switch
                        id={`env-${serviceKey}`}
                        checked={activeEnv === 'live'}
                        onCheckedChange={(checked) => 
                          toggleEnvironment(serviceKey, checked ? 'live' : 'test')
                        }
                      />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <Tabs value={environment} onValueChange={(v) => setEnvironment(v as 'test' | 'live')}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="test">
                      Environnement Test
                    </TabsTrigger>
                    <TabsTrigger value="live">
                      Environnement Live
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="test" className="space-y-4 mt-4">
                    {config.keys.map((keyConfig) => {
                      const keyId = getKeyId(serviceKey, keyConfig.name, 'test');
                      const value = getKeyValue(serviceKey, keyConfig.name, 'test');
                      const isVisible = visibleKeys.has(keyId);

                      return (
                        <div key={keyConfig.name} className="space-y-2">
                          <Label htmlFor={keyId}>{keyConfig.label}</Label>
                          <div className="flex gap-2">
                            {keyConfig.type === 'textarea' ? (
                              <Textarea
                                id={keyId}
                                value={isVisible ? value : maskKey(value, isVisible)}
                                onChange={(e) => setKeyValue(serviceKey, keyConfig.name, 'test', e.target.value)}
                                placeholder={keyConfig.placeholder}
                                className="font-mono text-sm"
                                rows={4}
                              />
                            ) : (
                              <Input
                                id={keyId}
                                type={isVisible ? 'text' : 'password'}
                                value={value}
                                onChange={(e) => setKeyValue(serviceKey, keyConfig.name, 'test', e.target.value)}
                                placeholder={keyConfig.placeholder}
                                className="font-mono text-sm"
                              />
                            )}
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={() => toggleVisibility(keyId)}
                            >
                              {isVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </Button>
                            {value && (
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={() => copyToClipboard(value)}
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                          {value && keyConfig.validation && !keyConfig.validation(value) && (
                            <p className="text-sm text-red-600 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              Format invalide pour {keyConfig.label}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </TabsContent>

                  <TabsContent value="live" className="space-y-4 mt-4">
                    {config.keys.map((keyConfig) => {
                      const keyId = getKeyId(serviceKey, keyConfig.name, 'live');
                      const value = getKeyValue(serviceKey, keyConfig.name, 'live');
                      const isVisible = visibleKeys.has(keyId);

                      return (
                        <div key={keyConfig.name} className="space-y-2">
                          <Label htmlFor={keyId}>{keyConfig.label}</Label>
                          <div className="flex gap-2">
                            {keyConfig.type === 'textarea' ? (
                              <Textarea
                                id={keyId}
                                value={isVisible ? value : maskKey(value, isVisible)}
                                onChange={(e) => setKeyValue(serviceKey, keyConfig.name, 'live', e.target.value)}
                                placeholder={keyConfig.placeholder}
                                className="font-mono text-sm"
                                rows={4}
                              />
                            ) : (
                              <Input
                                id={keyId}
                                type={isVisible ? 'text' : 'password'}
                                value={value}
                                onChange={(e) => setKeyValue(serviceKey, keyConfig.name, 'live', e.target.value)}
                                placeholder={keyConfig.placeholder}
                                className="font-mono text-sm"
                              />
                            )}
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={() => toggleVisibility(keyId)}
                            >
                              {isVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </Button>
                            {value && (
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={() => copyToClipboard(value)}
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                          {value && keyConfig.validation && !keyConfig.validation(value) && (
                            <p className="text-sm text-red-600 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              Format invalide pour {keyConfig.label}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      <div className="flex gap-4">
        <Button 
          onClick={saveKeys} 
          disabled={!hasChanges || saving}
          size="lg"
          className="flex-1"
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Enregistrement...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Sauvegarder les modifications
            </>
          )}
        </Button>
        {hasChanges && (
          <Button 
            variant="outline" 
            onClick={() => setEditedKeys({})}
            size="lg"
          >
            Annuler
          </Button>
        )}
      </div>
    </div>
  );
}
